import {
  carbonCropsEndpoints,
  CustomerEnabledCarbonCrop,
} from "portal/state/portal/carbonCrops";
import { DEBOUNCE_MS } from "portal/utils/timing";
import { getLocaleCarbonCropName } from "portal/components/carbonCrop/languages";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { UiCropFilterValue } from "portal/utils/categoryCollection/predictionPoints";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 25;

const toFilterOptions = (
  crops: CustomerEnabledCarbonCrop[],
  language: ReturnType<typeof portalLanguageOrDefault>,
  locale: string
): UiCropFilterValue[] =>
  crops
    .map<UiCropFilterValue>((crop) => ({
      id: crop.uuid,
      name: getLocaleCarbonCropName(crop, language),
      isCarbonCrop: true,
    }))
    .toSorted((a, b) => a.name.localeCompare(b.name, locale));

/**
 * Carbon crop options for the prediction-point crop filter, scoped to a
 * customer's enabled crops when one is in scope and to the full catalog
 * otherwise. Pages on demand, so callers must pair `loadMore` with an
 * infinite-scrolling listbox and feed typing back through
 * `onSearchInputChange` — the options array only holds fetched pages.
 */
export const useCustomerEnabledCarbonCrops = ({
  customerUuid,
  skip = false,
}: {
  customerUuid?: string;
  skip?: boolean;
}): {
  crops: UiCropFilterValue[] | undefined;
  isFetching: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  onSearchInputChange: (text: string) => void;
} => {
  const { i18n } = useTranslation();
  const language = portalLanguageOrDefault(i18n.language);
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebounce(searchInput.trim(), DEBOUNCE_MS);
  const search = searchText || undefined;
  const useCatalog = !skip && !customerUuid;

  const customerQuery =
    carbonCropsEndpoints.listCustomerEnabledCarbonCropsCursor.useInfiniteQuery(
      { customerUuid: customerUuid ?? "", limit: PAGE_SIZE, search },
      { skip: skip || !customerUuid }
    );
  const catalogQuery =
    carbonCropsEndpoints.listCarbonCropsCursor.useInfiniteQuery(
      { limit: PAGE_SIZE, search },
      { skip: !useCatalog }
    );

  const { data, isFetching, isError, hasNextPage, fetchNextPage } = useCatalog
    ? catalogQuery
    : customerQuery;

  const crops = useMemo(() => {
    if (!data) {
      return;
    }
    const flattened = data.pages.flatMap((page) => page.crops);
    return toFilterOptions(flattened, language, i18n.language);
  }, [data, i18n.language, language]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  const onSearchInputChange = setSearchInput;

  return {
    crops,
    isFetching,
    isError,
    hasMore: hasNextPage,
    loadMore,
    onSearchInputChange,
  };
};
